/**
 * Page wrapper Sale Control — bám `form_frmCtrlSalesMain.txt` (header + 10 nav tabs).
 *
 * - Header filters/exports khớp VBA: `Shipment Period`, `Is Finalized`, `Export (All)`,
 *   `Export (Filter)` cùng một hàng.
 * - Nav tabs: 10 tab theo đúng thứ tự Access (`navCtrlSalesLocalS` → `Preferences`).
 * - Mỗi tab render component riêng trong `frontend/src/pages/saleControl/`.
 */
import dayjs from 'dayjs'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import SaleReferencesView from './saleContract/SaleReferencesView'
import PageHeader from '../components/PageHeader'
import ReloadButton from '../components/ReloadButton'
import { usePageFilters } from '../hooks/usePageFilters'
import { useAppSelector } from '../store'
import { API_BASE_URL } from '../config/api'
import saleControlApi, {
  SALE_CONTROL_EXPORT_TOKEN,
  type SaleControlFinalizedFilter,
} from '../store/api/saleControlApi'

import ComingSoonPage from './ComingSoonPage'
import SaleControlClaimView from './saleControl/SaleControlClaimView'
import SaleControlCreditNoteView from './saleControl/SaleControlCreditNoteView'
import SaleControlDebitNoteView from './saleControl/SaleControlDebitNoteView'
import SaleControlDeliveryOrderView from './saleControl/SaleControlDeliveryOrderView'
import SaleControlInvoiceView from './saleControl/SaleControlInvoiceView'
import SaleControlLocalSalesView, { ALL_ROWS_PAGE_SIZE as SALE_CONTROL_PAGE_SIZE } from './saleControl/SaleControlLocalSalesView'
import SaleControlMovingView from './saleControl/SaleControlMovingView'
import SaleControlReceiveOrderView from './saleControl/SaleControlReceiveOrderView'
import SaleControlSalesAllocationView from './saleControl/SaleControlSalesAllocationView'
import {
  SALE_CONTROL_TAB_CONFIG,
  getSaleControlTab,
  isSaleControlTabKey,
  type SaleControlTabKey,
} from './saleControl/saleControlTabConfig'

interface SaleControlFilters extends Record<string, unknown> {
  startDate: string
  endDate: string
  isFinalized: SaleControlFinalizedFilter
}

const FILTER_PAGE_KEY = 'sales-control'

function getDefaultFilters(): SaleControlFilters {
  const now = dayjs()
  const fiscalYearStart = now.month() >= 9 ? dayjs(`${now.year()}-10-01`) : dayjs(`${now.year() - 1}-10-01`)
  const fiscalYearEnd = now.month() >= 9 ? dayjs(`${now.year() + 1}-09-30`) : dayjs(`${now.year()}-09-30`)

  return {
    startDate: fiscalYearStart.format('YYYY-MM-DD'),
    endDate: fiscalYearEnd.format('YYYY-MM-DD'),
    isFinalized: 'all',
  }
}

function normalizeStoredFilters(raw: unknown): SaleControlFilters {
  const defaults = getDefaultFilters()
  if (!raw || typeof raw !== 'object') return defaults
  const parsed = raw as Partial<SaleControlFilters>
  return {
    startDate: typeof parsed.startDate === 'string' && parsed.startDate ? parsed.startDate : defaults.startDate,
    endDate: typeof parsed.endDate === 'string' && parsed.endDate ? parsed.endDate : defaults.endDate,
    isFinalized:
      parsed.isFinalized === 'yes' || parsed.isFinalized === 'no' || parsed.isFinalized === 'all'
        ? parsed.isFinalized
        : 'all',
  }
}

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

// All Sale Control tabs are parked behind ComingSoonPage until the rebuild lands.
// Original view components (`SaleControlLocalSalesView`, `SaleControlSalesAllocationView`,
// `SaleControlDeliveryOrderView`, `SaleControlInvoiceView`, `SaleControlReceiveOrderView`,
// `SaleControlClaimView`, `SaleControlCreditNoteView`, `SaleControlDebitNoteView`,
// `SaleControlMovingView`, `SaleReferencesView`) + local-sales RTK selectors
// (`localSalesQueryArgs`, `localSalesSelect`, `localSalesFetching`) are still imported
// so restoring a tab is just swapping the body of renderTab back to the per-key switch.
//
// Original switch body (pre-park, restore as-is when rebuilding):
//   switch (key) {
//     case 'local-sales':
//       return (
//         <SaleControlLocalSalesView
//           startDate={filters.startDate}
//           endDate={filters.endDate}
//           isFinalized={filters.isFinalized}
//           invalidRange={invalidRange}
//         />
//       )
//     case 'sales-allocation':  return <SaleControlSalesAllocationView />
//     case 'delivery-order':    return <SaleControlDeliveryOrderView />
//     case 'invoice':           return <SaleControlInvoiceView />
//     case 'receive-order':     return <SaleControlReceiveOrderView />
//     case 'claim':             return <SaleControlClaimView />
//     case 'credit-note':       return <SaleControlCreditNoteView />
//     case 'debit-note':        return <SaleControlDebitNoteView />
//     case 'moving':            return <SaleControlMovingView />
//     case 'references':        return <SaleReferencesView />
//     default:                  return null
//   }
function renderTab(
  key: SaleControlTabKey,
  _filters: SaleControlFilters,
  _invalidRange: boolean,
) {
  return <ComingSoonPage title={getSaleControlTab(key).label} embedded />
}

export default function SaleControlPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const email = useAppSelector((s) => s.auth.user?.email ?? '')
  const { filters: storedFilters, setFilters: persistFilters } = usePageFilters<SaleControlFilters>(FILTER_PAGE_KEY)
  const filters = useMemo(() => normalizeStoredFilters(storedFilters), [storedFilters])
  const setFilters = persistFilters as (next: SaleControlFilters | ((prev: SaleControlFilters | undefined) => SaleControlFilters)) => void
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)

  const activeKey = useMemo<SaleControlTabKey>(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[2]
    return isSaleControlTabKey(candidate) ? candidate : 'local-sales'
  }, [location.pathname])

  useEffect(() => {
    if (!location.pathname.startsWith('/sales/control')) return

    const segments = location.pathname.split('/').filter(Boolean)
    if (!isSaleControlTabKey(segments[2])) {
      navigate('/sales/control/local-sales', { replace: true })
    }
  }, [location.pathname, navigate])

  const activeTab = SALE_CONTROL_TAB_CONFIG.find((tab) => tab.key === activeKey) ?? SALE_CONTROL_TAB_CONFIG[0]
  const hasInvalidRange = filters.startDate > filters.endDate

  // Đọc loading state của tab Local Sales từ cache RTK Query (chỉ tab này có query thật).
  // Args phải khớp với args mà SaleControlLocalSalesView dùng.
  const localSalesQueryArgs = useMemo(
    () => ({
      page: 1,
      pageSize: SALE_CONTROL_PAGE_SIZE,
      ...(hasInvalidRange ? {} : { startDate: filters.startDate, endDate: filters.endDate }),
      isFinalized: filters.isFinalized,
    }),
    [hasInvalidRange, filters.startDate, filters.endDate, filters.isFinalized],
  )
  const localSalesSelect = useMemo(
    () => saleControlApi.endpoints.getCtrlSalesLocalS.select(localSalesQueryArgs),
    [localSalesQueryArgs],
  )
  const localSalesFetching = useAppSelector((s) => localSalesSelect(s).status === 'pending')
  const reloadBusy = activeTab.key === 'local-sales' ? localSalesFetching : false

  const downloadSaleControlExcel = async (mode: 'all' | 'filter', url: string) => {
    if (!email) return

    setExporting(mode)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const downloadName =
        mode === 'filter' && filename
          ? filename.replace(/\.xlsx$/i, '_filter.xlsx')
          : filename
      a.href = href
      a.download = downloadName ?? (mode === 'filter' ? 'SaleControl_filter.xlsx' : 'SaleControl.xlsx')
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(null)
    }
  }

  /** Access `btnExportExcel_Click` — `/download/salecontrols/data`. */
  const handleExportAll = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/salecontrols/data?token=${encodeURIComponent(SALE_CONTROL_EXPORT_TOKEN)}&email=${encodeURIComponent(email)}`
    await downloadSaleControlExcel('all', url)
  }

  /** Access `btnExportExcellFilter_Click` — `/download/salecontrols/data_filter`. */
  const handleExportFilter = async () => {
    const params = new URLSearchParams({
      token: SALE_CONTROL_EXPORT_TOKEN,
      email,
      start_date: filters.startDate,
      end_date: filters.endDate,
      is_forced_close: filters.isFinalized === 'all' ? 'All' : filters.isFinalized === 'yes' ? 'Yes' : 'No',
    })
    const url = `${API_BASE_URL}/erp-legacy/download/salecontrols/data_filter?${params.toString()}`
    await downloadSaleControlExcel('filter', url)
  }

  const headerButtonSx = {
    minHeight: 24,
    px: 0.75,
    py: 0.15,
    fontSize: '0.67rem',
    lineHeight: 1.1,
    textTransform: 'none' as const,
  }

  const headerLabelSx = {
    whiteSpace: 'nowrap',
    fontSize: '0.72rem',
    lineHeight: 1.1,
  }

  const compactInputSx = {
    width: { xs: 124, sm: 132 },
    '& .MuiInputBase-root': {
      minHeight: 26,
      fontSize: '0.72rem',
    },
    '& .MuiInputBase-input': {
      py: 0.25,
      px: 0.75,
    },
  }

  const compactSelectSx = {
    width: 94,
    '& .MuiInputBase-root': {
      minHeight: 26,
      fontSize: '0.72rem',
    },
    '& .MuiSelect-select': {
      py: 0.25,
      px: 0.75,
    },
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Sale Control"
        module="Sales"
        actions={
          <Stack
            direction="row"
            spacing={0.45}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{ width: '100%' }}
          >
            <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
                Shipment Period:
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format="DD-MMM-YYYY"
                  value={filters.startDate ? dayjs(filters.startDate) : null}
                  onChange={(d) =>
                    setFilters((prev) => ({
                      ...getDefaultFilters(),
                      ...prev,
                      startDate: d ? d.format('YYYY-MM-DD') : getDefaultFilters().startDate,
                    }))
                  }
                  slotProps={{ textField: { size: 'small', sx: compactInputSx, inputProps: { 'aria-label': 'Shipment Period start date' } } }}
                />
                <DatePicker
                  format="DD-MMM-YYYY"
                  value={filters.endDate ? dayjs(filters.endDate) : null}
                  onChange={(d) =>
                    setFilters((prev) => ({
                      ...getDefaultFilters(),
                      ...prev,
                      endDate: d ? d.format('YYYY-MM-DD') : getDefaultFilters().endDate,
                    }))
                  }
                  slotProps={{ textField: { size: 'small', sx: compactInputSx, inputProps: { 'aria-label': 'Shipment Period end date' } } }}
                />
              </LocalizationProvider>
            </Stack>

            <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
                Is Finalized:
              </Typography>
              <TextField
                select
                size="small"
                value={filters.isFinalized}
                onChange={(event) =>
                  setFilters((prev) => ({ ...getDefaultFilters(), ...prev, isFinalized: event.target.value as SaleControlFinalizedFilter }))
                }
                sx={compactSelectSx}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Stack>

            <ReloadButton
              sx={headerButtonSx}
              apiTags={['SaleControl']}
              baseTags={[]}
              busy={reloadBusy}
            />
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportFilter}
              disabled={exporting !== null || !email || hasInvalidRange}
              startIcon={
                exporting === 'filter' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
            >
              Export (Filter)
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportAll}
              disabled={exporting !== null || !email}
              startIcon={
                exporting === 'all' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
            >
              Export (All)
            </Button>
          </Stack>
        }
      />
      {hasInvalidRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value: SaleControlTabKey) => {
            navigate(`/sales/control/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 38,
            '& .MuiTabs-indicator': { height: 2 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.25,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          {SALE_CONTROL_TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>

        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 2 }}>
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
            {renderTab(activeKey, filters, hasInvalidRange)}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}
